import React, { useState } from 'react';
import './AdminPinSettings.css';
import { FaLock, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { Shield, Key, Search, Lock as LockIcon, UserCheck, Settings, AlertCircle, UserPlus, UserMinus } from 'lucide-react';
import API_BASE_URL from '../../../config/api';

const API_BASE = API_BASE_URL;

const cleanEmail = (email) => {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const cleanLocal = local.split('+')[0];
  return `${cleanLocal}@${domain}`;
};

const AdminPinSettings = ({ isSuperAdmin }) => {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [privilegedUsers, setPrivilegedUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Super Admin management state
  const [adminsList, setAdminsList] = useState([]);
  const [superAdminLoading, setSuperAdminLoading] = useState({});
  const [superAdminMsg, setSuperAdminMsg] = useState(null);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');

  const filteredUsers = privilegedUsers.filter(user =>
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAdmins = adminsList.filter(a =>
    `${a.firstName} ${a.lastName}`.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(adminSearchTerm.toLowerCase())
  );

  React.useEffect(() => {
    const userId = localStorage.getItem('userId');
    setCurrentUserId(parseInt(userId));
    fetchPrivilegedUsers();
    if (isSuperAdmin) fetchAdmins();
  }, [isSuperAdmin]);

  const fetchPrivilegedUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/auth/privileged-users-pin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPrivilegedUsers(data);
      }
    } catch (err) {
      console.error('Error fetching privileged users:', err);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/users/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminsList(data);
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  };

  const handleToggleSuperAdmin = async (admin) => {
    setSuperAdminLoading(prev => ({ ...prev, [admin.id]: true }));
    setSuperAdminMsg(null);
    const token = localStorage.getItem('token');
    const method = admin.isSuperAdmin ? 'DELETE' : 'POST';

    try {
      const res = await fetch(`${API_BASE}/api/users/${admin.id}/super-admin`, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setSuperAdminMsg({ type: 'success', text: data.message });
        fetchAdmins(); // refresh list
      } else {
        setSuperAdminMsg({ type: 'error', text: data.message || 'Ocorreu um erro.' });
      }
    } catch {
      setSuperAdminMsg({ type: 'error', text: 'Erro de ligação ao servidor.' });
    } finally {
      setSuperAdminLoading(prev => ({ ...prev, [admin.id]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (newPin.length !== 4) {
      setError('O novo PIN deve ter exatamente 4 dígitos.');
      return;
    }

    if (newPin !== confirmNewPin) {
      setError('A confirmação do novo PIN não coincide.');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/auth/change-admin-pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPin, newPin, confirmNewPin })
      });

      if (response.ok) {
        setMessage('PIN de administração alterado com sucesso.');
        setCurrentPin('');
        setNewPin('');
        setConfirmNewPin('');
      } else {
        const err = await response.json();
        setError(err.message || 'Erro ao alterar o PIN. Verifique se o PIN atual está correto.');
      }
    } catch (err) {
      console.error('Error changing PIN:', err);
      setError('Erro de ligação ao servidor.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinChange = (setter) => (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 4) setter(val);
  };

  return (
    <div className="admin-pin-settings">
      <div className="admin-pin-settings-header">
        <div className="admin-pin-settings-header-icon">
          <FaLock />
        </div>
        <div>
          <h2>Segurança e Acesso</h2>
          <p>Altere o seu PIN de 4 dígitos para acesso à dashboard de administração.</p>
        </div>
      </div>

      <div className="admin-pin-settings-content">

        {/* ── Super Admin Management (SuperAdmin only) ── */}
        {isSuperAdmin && (
          <div className="superadmin-section">
            <div className="security-card superadmin-card">
              <div className="card-header">
                <div className="card-icon-title">
                  <div>
                    <h3>Gestão de Administradores</h3>
                    <p>Atribua ou revogue o acesso a funcionalidades sensíveis (ex: Análise financeira).</p>
                  </div>
                </div>
              </div>

              {superAdminMsg && (
                <div className={`superadmin-msg ${superAdminMsg.type}`}>
                  {superAdminMsg.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                  {superAdminMsg.text}
                </div>
              )}

              <div className="card-search-container">
                <div className="search-input-wrapper">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Pesquisar administrador..."
                    value={adminSearchTerm}
                    onChange={(e) => setAdminSearchTerm(e.target.value)}
                    className="user-search-input"
                  />
                </div>
              </div>

              <div className="superadmin-list">
                {filteredAdmins.length === 0 ? (
                  <div className="empty-search-state">
                    <AlertCircle size={24} />
                    <p>Nenhum administrador encontrado.</p>
                  </div>
                ) : (
                  filteredAdmins.map(admin => {
                    const isMe = admin.id === currentUserId;
                    const isLoadingThis = superAdminLoading[admin.id];
                    return (
                      <div key={admin.id} className={`superadmin-item ${admin.isSuperAdmin ? 'is-super' : ''} ${isMe ? 'is-me' : ''}`}>
                        <div className="user-info">
                          <div className={`user-avatar-premium ${admin.isSuperAdmin ? 'super-avatar' : ''}`}>
                            <span>{`${admin.firstName} ${admin.lastName}`.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</span>
                          </div>
                          <div className="user-details">
                            <div className="name-row">
                              <span className="user-name">{admin.firstName} {admin.lastName}</span>
                              {isMe && <span className="me-badge-premium">EU</span>}
                              {admin.isSuperAdmin && (
                                <span className="superadmin-badge">
                                  Admin
                                </span>
                              )}
                            </div>
                            <span className="user-email">{cleanEmail(admin.email)}</span>
                          </div>
                        </div>

                        <div className="superadmin-action">
                          {isMe && admin.isSuperAdmin ? (
                            <div className="self-revoke-warning" title="Não pode revogar o seu próprio role">
                              <LockIcon size={14} />
                              <span>Não revogável</span>
                            </div>
                          ) : (
                            <button
                              className={`btn-toggle-superadmin ${admin.isSuperAdmin ? 'revoke' : 'promote'}`}
                              onClick={() => handleToggleSuperAdmin(admin)}
                              disabled={isLoadingThis}
                              title={admin.isSuperAdmin ? 'Revogar Admin' : 'Promover Admin'}
                            >
                              {isLoadingThis ? (
                                <span className="btn-spinner" />
                              ) : admin.isSuperAdmin ? (
                                <><UserMinus size={14} /> Revogar</>
                              ) : (
                                <><UserPlus size={14} /> Promover</>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Existing grid ── */}
        <div className="security-grid">
          {/* Left Column: Privileged Users List */}
          <div className="security-card privileged-users-card">
            <div className="card-header">
              <div className="card-icon-title">
                <Shield className="card-header-icon" />
                <div>
                  <h3>Acessos Restritos</h3>
                  <p>Administradores e Treinadores com acesso restrito.</p>
                </div>
              </div>
            </div>

            <div className="card-search-container">
              <div className="search-input-wrapper">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Pesquisar por nome ou cargo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="user-search-input"
                />
              </div>
            </div>

            <div className="privileged-users-list">
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div key={user.userId} className={`user-pin-item ${user.userId === currentUserId ? 'is-me' : ''}`}>
                    <div className="user-info">
                      <div className="user-avatar-premium">
                        <span>{user.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</span>
                      </div>
                      <div className="user-details">
                        <div className="name-row">
                          <span className="user-name">{user.fullName}</span>
                          {user.userId === currentUserId && <span className="me-badge-premium">EU</span>}
                        </div>
                        <div className="role-row">
                          <span className={`role-badge-refined ${user.role.toLowerCase()}`}>{user.role}</span>
                        </div>
                      </div>
                    </div>

                    <div className="pin-status-wrapper">
                      {user.hasPersonalPin ? (
                        <div className="status-indicator-premium customized">
                          <UserCheck size={14} />
                          <span>Personalizado</span>
                        </div>
                      ) : (
                        <div className="status-indicator-premium default">
                          <LockIcon size={14} />
                          <span>Padrão</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-search-state">
                  <AlertCircle size={24} />
                  <p>Nenhum utilizador encontrado para "{searchTerm}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Information and Change PIN */}
          <div className="security-sidebar">
            <div className="security-card info-card-premium">
              <div className="info-header">
                <AlertCircle className="info-icon-large" />
                <h4>Segurança do PIN</h4>
              </div>
              <p>O PIN é uma camada extra de proteção. Recomendamos que cada utilizador defina um código pessoal único de 4 dígitos.</p>
              <div className="pin-tip">
                <strong>Dica:</strong> Evite sequências simples como "1234" ou "0000".
              </div>
            </div>

            <div className="security-card change-pin-card">
              <div className="card-header">
                <div className="card-icon-title">
                  <Settings className="card-header-icon" />
                  <div>
                    <h3>Alterar o Meu PIN</h3>
                    <p>Defina o seu código de acesso pessoal.</p>
                  </div>
                </div>
              </div>

              {message && (
                <div className="premium-alert success">
                  <FaCheckCircle /> {message}
                </div>
              )}

              {error && (
                <div className="premium-alert error">
                  <FaExclamationCircle /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="premium-pin-form">
                <div className="premium-form-group">
                  <label>PIN Atual</label>
                  <div className="input-with-icon">
                    <LockIcon size={18} className="input-icon" />
                    <input
                      type="password"
                      value={currentPin}
                      onChange={handlePinChange(setCurrentPin)}
                      placeholder="••••"
                      maxLength={4}
                      inputMode="numeric"
                      required
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="premium-form-group">
                  <label>Novo PIN</label>
                  <div className="input-with-icon">
                    <Key size={18} className="input-icon" />
                    <input
                      type="password"
                      value={newPin}
                      onChange={handlePinChange(setNewPin)}
                      placeholder="••••"
                      maxLength={4}
                      inputMode="numeric"
                      required
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="premium-form-group">
                  <label>Confirmar PIN</label>
                  <div className="input-with-icon">
                    <UserCheck size={18} className="input-icon" />
                    <input
                      type="password"
                      value={confirmNewPin}
                      onChange={handlePinChange(setConfirmNewPin)}
                      placeholder="••••"
                      maxLength={4}
                      inputMode="numeric"
                      required
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-premium-submit"
                  disabled={isLoading || !currentPin || newPin.length !== 4 || confirmNewPin.length !== 4}
                >
                  {isLoading ? 'A processar...' : 'Atualizar PIN'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPinSettings;
